import React, { useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Search } from '@modernloop/shared/components';
import { AttributeIcon, DirectoryIcon } from '@modernloop/shared/icons';
import { Button, Grid, Paper, Tab, TabProps, Tabs } from '@mui/material';
import { isEmpty } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

import {
  EmployeeFilterFragment,
  InterviewModuleMemberSearchOrderByProperty,
  Sort,
  useInterviewModuleMemberSearchLazyQuery,
} from 'src/generated/mloop-graphql';

import { EmployeeFilter } from 'src/entities/InterviewPlan/InterviewSeat/ModuleMemberFilters';

import { JobStageInterviewSeat } from 'src/store/slices/job-stage-interview-seat';

import { DEBOUNCE_TIMEOUT, PAGE_SIZE } from 'src/constants';

import AttributesFilter from './AttributesFilter';

enum EmployeeAndAttributesFilterTabs {
  ATTRIBUTES = 'attributes',
  PEOPLE = 'people',
}

type Props = {
  interviewModuleMembers: EmployeeFilterFragment[];
  interviewModuleId: string;
  seat: JobStageInterviewSeat;
  onUpdated: (seat: JobStageInterviewSeat) => void;
};

const useSxProps = () => {
  return useMemo(() => {
    return {
      paper: {
        minWidth: '360px',
        padding: 1.5,
      },
    };
  }, []);
};

const EmployeeAndAttributesFilter = ({
  interviewModuleMembers,
  interviewModuleId,
  seat,
  onUpdated,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const sxProps = useSxProps();

  const TABS: TabProps[] = useMemo(
    () => [
      {
        value: EmployeeAndAttributesFilterTabs.PEOPLE,
        label: t('hiring-dashboard-filter:filter.tabPeople'),
      },
      {
        value: EmployeeAndAttributesFilterTabs.ATTRIBUTES,
        label: t('hiring-dashboard-filter:filter.attributes'),
      },
    ],
    [t]
  );

  const [attributesEmpty, setAttributesEmpty] = useState(false);
  const [activeTab, setActiveTab] = useState(EmployeeAndAttributesFilterTabs.PEOPLE);
  const [filterText, setFilterText] = useState('');

  const [searchInterviewModuleMembers, { data }] = useInterviewModuleMemberSearchLazyQuery();

  // Hide button if there are no selected values
  const showClearButton =
    (seat.selectedReverseShadowInterviewerIds?.length ?? 0) > 0 ||
    (seat.selectedShadowInterviewerIds?.length ?? 0) > 0 ||
    (seat.selectedTrainedInterviewerIds?.length ?? 0) > 0 ||
    Object.values(seat.attributeMap ?? {})?.length > 0;

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect bridges form state with changing inputs from outside this component.
  useEffect(() => {
    // If attributes are selected then default to Attributes tab
    if (!isEmpty(seat.attributeMap)) {
      setActiveTab(EmployeeAndAttributesFilterTabs.ATTRIBUTES);
    }
  }, [seat]);

  const handleClearAll = () => {
    onUpdated({
      ...seat,
      selectedReverseShadowInterviewerIds: undefined,
      selectedShadowInterviewerIds: undefined,
      selectedTrainedInterviewerIds: undefined,
      attributeMap: undefined,
    });
  };

  const handleActiveTabChange = (option: string) => {
    setFilterText('');
    handleClearAll();
    setActiveTab(option as EmployeeAndAttributesFilterTabs);
  };

  const debouncedSearchInterviewModuleMembers = useDebouncedCallback(searchInterviewModuleMembers, DEBOUNCE_TIMEOUT);

  const handleFilterTextChange = (search: string) => {
    debouncedSearchInterviewModuleMembers({
      variables: {
        input: {
          pageInput: {
            limit: PAGE_SIZE,
            offset: 0,
          },
          interviewModuleId,
          search,
          isArchived: false,
          isAtsDisabled: false,
          isDirectoryDisabled: false,
          orderBy: {
            property: InterviewModuleMemberSearchOrderByProperty.ProgressPercentage,
            direction: Sort.Desc,
          },
        },
      },
    });
    setFilterText(search);
  };

  const tabs = TABS.map((tab) => {
    switch (tab.value) {
      case EmployeeAndAttributesFilterTabs.ATTRIBUTES: {
        if (attributesEmpty) {
          return null;
        }

        if (activeTab === tab.value) {
          tab.icon = <AttributeIcon color="primary" />;
        }
        return tab;
      }
      case EmployeeAndAttributesFilterTabs.PEOPLE: {
        if (activeTab === tab.value) {
          tab.icon = <DirectoryIcon color="primary" />;
        }
        return tab;
      }
      default:
        return null;
    }
  }).filter((tab) => !!tab) as TabProps[];

  const handleNoAttributes = () => {
    setAttributesEmpty(true);
    setActiveTab(EmployeeAndAttributesFilterTabs.PEOPLE);
  };

  if (!tabs) {
    return null;
  }

  // before the user has searched for moduleMembers, use the interviewModuleMembers passed from the parent query
  const moduleMemberSearchResults = data?.interviewModuleMemberSearch?.items;
  const displayedInterviewModuleMembers = moduleMemberSearchResults || interviewModuleMembers;

  return (
    <Paper sx={sxProps.paper}>
      <Grid container direction="column" spacing={1}>
        <Grid item spacing={0}>
          <Search
            autoFocus
            fullWidth
            placeholder={
              activeTab === EmployeeAndAttributesFilterTabs.PEOPLE
                ? t('hiring-dashboard-filter:filter.searchInterviewers')
                : t('hiring-dashboard-filter:filter.searchAttributes')
            }
            value={filterText}
            onChange={handleFilterTextChange}
          />
        </Grid>

        <Grid item container justifyContent="space-between" spacing={0}>
          <Grid item spacing={0}>
            <Tabs value={activeTab} onChange={(_, value) => handleActiveTabChange(value as string)}>
              {tabs.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
          </Grid>
          {showClearButton && (
            <Grid item spacing={0}>
              <Button size="small" onClick={handleClearAll} variant="text" color="primary">
                {t('hiring-dashboard-filter:filter.clearSelection')}
              </Button>
            </Grid>
          )}
        </Grid>

        <Grid item spacing={0}>
          {activeTab === EmployeeAndAttributesFilterTabs.ATTRIBUTES && (
            <AttributesFilter
              seat={seat}
              filterText={filterText}
              onNoAttributes={handleNoAttributes}
              onUpdated={onUpdated}
            />
          )}
          {activeTab === EmployeeAndAttributesFilterTabs.PEOPLE && (
            <EmployeeFilter
              interviewModuleMembers={displayedInterviewModuleMembers}
              seat={seat}
              onUpdated={onUpdated}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

EmployeeAndAttributesFilter.fragment = {
  employeeAndAttributesFilter: gql`
    ${EmployeeFilter.fragments?.interviewModuleMembers}
    fragment EmployeeAndAttributesFilter on InterviewModuleMember {
      ...EmployeeFilter
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ModuleMemberSearch = gql`
  ${EmployeeFilter.fragments?.interviewModuleMembers}
  query InterviewModuleMemberSearch($input: InterviewModuleMemberSearchInput!) {
    interviewModuleMemberSearch(input: $input) {
      items {
        ...EmployeeFilter
      }
    }
  }
`;

export default EmployeeAndAttributesFilter;
